import { getScrollContainer, getScrollTop, scrollTo as scrollPage } from '@theme/scroll-container';
const BMW_SERIES_ORDER = [
  '1 Series',
  '2 Series',
  '3 Series',
  '4 Series',
  '5 Series',
  '6 Series',
  '7 Series',
  '8 Series',
  'X1',
  'X2',
  'X3',
  'X4',
  'X5',
  'X6',
  'X7',
  'Z3',
  'Z4',
];

function normalize(value) {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
}

function normalizeSearchValue(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

function searchTokens(value) {
  return normalizeSearchValue(value).split(/\s+/).filter(Boolean);
}

function parseSearchQuery(value) {
  const tokens = searchTokens(value);
  const seriesIndex = tokens.findIndex((token, index) => (
    /^[1-8]$/.test(token) && /^SER(?:I(?:E(?:S)?)?)?$/.test(tokens[index + 1] ?? '')
  ));

  if (seriesIndex === -1) return { tokens, series: '' };

  return {
    tokens: tokens.filter((_, index) => index !== seriesIndex && index !== seriesIndex + 1),
    series: `${tokens[seriesIndex]} SERIES`,
  };
}

function badgeAliasPrefixes(value) {
  return String(value ?? '')
    .split(';')
    .map((alias) => normalizeSearchValue(alias).replace(/\s+/g, ''))
    .filter(Boolean);
}

function matchesSearchText(searchable, query, options = {}) {
  const { tokens, series } = parseSearchQuery(query);
  if (!tokens.length && !series) return true;

  const chassis = normalize(options.chassis);
  if (!series && tokens.length === 1 && /^\d{1,2}$/.test(tokens[0])) {
    return chassis.includes(tokens[0]);
  }

  if (series && normalizeSearchValue(options.series) !== series) {
    return false;
  }

  const normalized = normalizeSearchValue(searchable);
  const words = new Set(normalized.split(/\s+/).filter(Boolean));
  const compact = normalized.replace(/\s+/g, '');
  const badgePrefixes = badgeAliasPrefixes(options.badgeAliases);
  const yearFrom = Number.parseInt(options.yearFrom, 10);
  const parsedYearTo = Number.parseInt(options.yearTo, 10);
  const yearTo = Number.isFinite(parsedYearTo) ? parsedYearTo : new Date().getFullYear();

  return tokens.every((token) => {
    if (/^\d{4}$/.test(token) && Number.isFinite(yearFrom)) {
      const year = Number.parseInt(token, 10);
      if (year >= yearFrom && year <= yearTo) return true;
    }
    if (words.has(token) || (token.length >= 5 && compact.includes(token))) return true;
    return token.length >= 3 && badgePrefixes.some((alias) => alias.startsWith(token));
  });
}

function recordSearchText(record) {
  return [
    record.series,
    record.chassis,
    record.friendlyLabel,
    record.primaryDisplay,
    record.bodyStyle,
    record.yearRange,
    record.badgeAliases,
    record.searchAliases,
    record.marketNote,
    record.yearNote,
  ].filter(Boolean).join(' ');
}

function recordMatchesSearch(record, query) {
  return matchesSearchText(recordSearchText(record), query, {
    chassis: record.chassis,
    series: record.series,
    yearFrom: record.yearFrom,
    yearTo: record.yearTo,
    badgeAliases: record.badgeAliases,
  });
}

function matchedBadgeAliases(value, query, options = {}) {
  const seriesTokens = new Set(searchTokens(options.series));
  const badgeTokens = searchTokens(query).filter((token) => {
    if (!/\d/.test(token) || /^\d{4}$/.test(token)) return false;
    return !seriesTokens.has(token);
  });

  if (!badgeTokens.length) return [];

  return String(value ?? '')
    .split(';')
    .map((alias) => alias.trim())
    .filter(Boolean)
    .filter((alias) => {
      const normalized = normalizeSearchValue(alias);
      const words = new Set(normalized.split(/\s+/).filter(Boolean));
      const compact = normalized.replace(/\s+/g, '');

      return badgeTokens.some((token) => {
        if (words.has(token)) return true;
        return token.length >= 3 && compact.startsWith(token);
      });
    });
}

function customerDisplayLabel(value) {
  return String(value ?? '').replace(/\s+\(incl\.\s+[^)]+\)(?=\s*·)/gi, '');
}

function recordFriendlyLabel(record) {
  return customerDisplayLabel(record.friendlyLabel || `${normalizeBmwSeries(record.series)} · ${record.chassis}`);
}

function recordPrimaryDisplay(record) {
  return record.primaryDisplay || `BMW ${normalizeBmwSeries(record.series)}`;
}

function parseJson(element, fallback) {
  try {
    return JSON.parse(element?.textContent ?? '');
  } catch (_error) {
    return fallback;
  }
}

function normalizeBmwSeries(value) {
  const series = String(value ?? '').trim().split(/\s*\/\s*/)[0];
  const aliases = {
    M2: '2 Series',
    M3: '3 Series',
    M4: '4 Series',
    M5: '5 Series',
    M6: '6 Series',
    M8: '8 Series',
    'X3 M': 'X3',
    'X4 M': 'X4',
    'X5 M': 'X5',
    X5L: 'X5',
    'X6 M': 'X6',
  };

  return aliases[series.toUpperCase()] ?? series;
}

function uniqueFitmentEntries(entries) {
  const seen = new Set();

  return entries.filter(({ record }) => {
    const key = `${normalizeBmwSeries(record.series)}|${normalize(record.chassis)}|${normalize(record.diameter)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

class CentreCapBmwFitment extends HTMLElement {
  records = [];
  productData = {};
  selectedRecord = null;
  returnFocus = null;
  abortController;
  pickerObserver;
  badgeFrame;
  applyingDiameter = false;
  appliedDiameter = null;

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.records = parseJson(this.querySelector('[data-bmw-fitment-data]'), []);
    this.productData = parseJson(this.querySelector('[data-bmw-product-data]'), {});
    if (!Array.isArray(this.records) || !this.records.length) return;

    this.populateSeries();

    this.addEventListener('click', this.handleClick, { signal });
    this.querySelector('[data-bmw-series]')?.addEventListener('change', this.handleSeriesChange, { signal });
    this.querySelector('[data-bmw-chassis]')?.addEventListener('change', this.handleChassisChange, { signal });
    this.querySelector('[data-bmw-search]')?.addEventListener('input', this.handleSearch, { signal });
    this.querySelector('[data-bmw-dialog]')?.addEventListener('cancel', this.handleCancel, { signal });

    const product = this.closest('.centre-cap-pdp');
    if (product) {
      product.addEventListener('change', (event) => {
        if (!this.applyingDiameter && event.target?.closest?.('variant-picker')) this.appliedDiameter = null;
        this.requestBadgeUpdate();
      }, { signal });
      this.pickerObserver = new MutationObserver(() => this.requestBadgeUpdate());
      this.pickerObserver.observe(product, { childList: true, subtree: true });
    }

  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.pickerObserver?.disconnect();
    window.cancelAnimationFrame(this.badgeFrame);
    document.documentElement.classList.remove('centre-cap-bmw-dialog-open');
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const open = event.target.closest('[data-bmw-open]');
    if (open instanceof HTMLElement) {
      this.openDialog(open);
      return;
    }

    if (event.target.closest('[data-bmw-close]')) {
      this.closeDialog();
      return;
    }

    const mode = event.target.closest('[data-bmw-mode]');
    if (mode instanceof HTMLButtonElement) {
      this.showMode(mode.dataset.bmwMode);
      return;
    }

    const match = event.target.closest('[data-bmw-match-index]');
    if (match instanceof HTMLButtonElement) {
      const record = this.records[Number(match.dataset.bmwMatchIndex)];
      if (record) this.selectRecord(record);
      return;
    }

    if (event.target.closest('[data-bmw-select-size]')) {
      this.selectDiameter();
      return;
    }

    const dialog = this.querySelector('[data-bmw-dialog]');
    if (event.target === dialog) this.closeDialog();
  };

  handleSeriesChange = (event) => {
    if (!(event.currentTarget instanceof HTMLSelectElement)) return;
    this.populateChassis(event.currentTarget.value);
  };

  handleChassisChange = (event) => {
    if (!(event.currentTarget instanceof HTMLSelectElement) || !event.currentTarget.value) return;
    const record = this.records[Number(event.currentTarget.value)];
    if (record) this.selectRecord(record);
  };

  handleSearch = (event) => {
    if (!(event.currentTarget instanceof HTMLInputElement)) return;
    this.renderSearchMatches(event.currentTarget.value);
  };

  handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  populateSeries() {
    const select = this.querySelector('[data-bmw-series]');
    if (!(select instanceof HTMLSelectElement)) return;

    const availableSeries = new Set(this.records.map((record) => normalizeBmwSeries(record.series)));
    const series = BMW_SERIES_ORDER.filter((name) => availableSeries.has(name));
    series.forEach((name) => {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = `BMW ${name}`;
      select.append(option);
    });
  }

  populateChassis(series, selectedChassis = '') {
    const select = this.querySelector('[data-bmw-chassis]');
    if (!(select instanceof HTMLSelectElement)) return;

    while (select.options.length > 1) select.remove(1);
    const matchingRecords = uniqueFitmentEntries(this.records
      .map((record, index) => ({ record, index }))
      .filter(({ record }) => normalizeBmwSeries(record.series) === series));

    matchingRecords.forEach(({ record, index }) => {
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = recordFriendlyLabel(record);
      option.selected = record.chassis === selectedChassis;
      select.append(option);
    });

    select.disabled = matchingRecords.length === 0;
    if (!selectedChassis) select.selectedIndex = 0;
  }

  showMode(mode = 'browse') {
    this.querySelectorAll('[data-bmw-mode]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.bmwMode === mode));
    });
    this.querySelectorAll('[data-bmw-panel]').forEach((panel) => {
      panel.toggleAttribute('hidden', panel.dataset.bmwPanel !== mode);
    });

    const focusTarget = mode === 'search'
      ? this.querySelector('[data-bmw-search]')
      : this.querySelector('[data-bmw-series]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }

  openDialog(trigger) {
    const dialog = this.querySelector('[data-bmw-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.returnFocus = trigger;
    document.documentElement.classList.add('centre-cap-bmw-dialog-open');
    if (!dialog.open) dialog.showModal();
    requestAnimationFrame(() => {
      if (!dialog.open) return;
      const selected = this.selectedRecord
        ? this.querySelector('[data-bmw-chassis]')
        : this.querySelector('[data-bmw-series]');
      if (selected instanceof HTMLElement) selected.focus();
    });
  }

  closeDialog(restoreFocus = true) {
    const dialog = this.querySelector('[data-bmw-dialog]');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    document.documentElement.classList.remove('centre-cap-bmw-dialog-open');
    if (restoreFocus) this.returnFocus?.focus();
    this.returnFocus = null;
  }

  renderSearchMatches(query) {
    const container = this.querySelector('[data-bmw-matches]');
    const summary = this.querySelector('[data-bmw-match-summary]');
    const list = this.querySelector('[data-bmw-match-list]');
    if (!(container instanceof HTMLElement) || !(list instanceof HTMLUListElement)) return;

    list.replaceChildren();
    const tokens = searchTokens(query);

    if (!tokens.length) {
      container.hidden = true;
      if (summary) summary.textContent = '';
      return;
    }

    const matches = uniqueFitmentEntries(this.records
      .map((record, index) => ({ record, index }))
      .filter(({ record }) => recordMatchesSearch(record, query)))
      .sort((a, b) => {
        const compactQuery = tokens.join('');
        const aExact = compactQuery === normalize(a.record.chassis) ? 0 : 1;
        const bExact = compactQuery === normalize(b.record.chassis) ? 0 : 1;
        return aExact - bExact;
      });

    const copy = this.productData.copy ?? {};
    container.hidden = false;
    if (summary) summary.textContent = matches.length ? `${copy.matches}: ${matches.length}` : copy.noMatches;

    matches.forEach(({ record, index }) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      const primary = document.createElement('span');
      const matchedAliases = matchedBadgeAliases(record.badgeAliases, query, record);
      const technical = document.createElement('span');
      button.type = 'button';
      button.dataset.bmwMatchIndex = String(index);
      primary.textContent = recordFriendlyLabel(record);
      technical.textContent = `${record.chassis} · ${record.diameter}`;
      primary.className = 'centre-cap-bmw-finder__match-label';
      technical.className = 'centre-cap-bmw-finder__match-technical';
      button.append(primary);
      if (matchedAliases.length && copy.matchReason) {
        const matchReason = document.createElement('span');
        matchReason.className = 'centre-cap-bmw-finder__match-reason';
        matchReason.textContent = `${copy.matchReason}: ${matchedAliases.join(', ')}`;
        button.append(matchReason);
      }
      button.append(technical);
      item.append(button);
      list.append(item);
    });
  }

  selectRecord(record) {
    this.appliedDiameter = null;
    this.selectedRecord = record;
    const series = normalizeBmwSeries(record.series);

    const result = this.querySelector('[data-bmw-result]');
    const heading = this.querySelector('[data-bmw-result-heading]');
    const vehicle = this.querySelector('[data-bmw-result-vehicle]');
    const chassis = this.querySelector('[data-bmw-result-chassis]');
    const diameter = this.querySelector('[data-bmw-result-diameter]');
    const message = this.querySelector('[data-bmw-result-copy]');
    const selectButton = this.querySelector('[data-bmw-select-size]');
    const unavailable = this.querySelector('[data-bmw-unavailable]');
    const unavailableCopy = this.querySelector('[data-bmw-unavailable-copy]');
    const unavailableLink = this.querySelector('[data-bmw-unavailable-link]');
    const copy = this.productData.copy ?? {};
    const offeredDiameters = Array.isArray(this.productData.diameters) ? this.productData.diameters.map(normalize) : [];
    const isOffered = offeredDiameters.includes(normalize(record.diameter));
    const product = this.closest('.centre-cap-pdp');
    const matchingControl = Array.from(product?.querySelectorAll('variant-picker [data-option-name]') ?? []).find((option) =>
      normalize(option.dataset.optionName) === 'DIAMETER' && normalize(option.value) === normalize(record.diameter)
    );
    const isAvailable = matchingControl
      ? matchingControl.dataset.optionAvailable !== 'false'
      : this.productData.singleAvailable === true;
    const canAutoSelect = isOffered && isAvailable && record.status !== 'measure';

    if (result instanceof HTMLElement) {
      result.hidden = false;
      result.dataset.status = record.status;
    }
    if (heading) heading.textContent = copy.yourBmw;
    if (vehicle) vehicle.textContent = recordPrimaryDisplay(record);
    if (chassis) chassis.textContent = record.chassis;
    if (diameter) diameter.textContent = record.diameter;
    if (message) message.textContent = copy[record.status] ?? '';

    if (selectButton instanceof HTMLButtonElement) {
      selectButton.hidden = !canAutoSelect;
      selectButton.textContent = `${copy.selectSize} ${record.diameter} →`;
    }
    if (unavailable instanceof HTMLElement) unavailable.hidden = isOffered && isAvailable;
    if ((!isOffered || !isAvailable) && unavailableCopy) {
      unavailableCopy.textContent = `${copy.unavailablePrefix} ${record.diameter}. ${copy.unavailableSuffix} ${record.diameter}.`;
    }
    if ((!isOffered || !isAvailable) && unavailableLink instanceof HTMLAnchorElement) {
      unavailableLink.href = this.productData.sizeUrls?.[record.diameter] ?? '/collections/bmw-centre-caps';
      unavailableLink.textContent = `${copy.viewSizePrefix} ${record.diameter} ${copy.viewSizeSuffix}`;
    }

    const seriesSelect = this.querySelector('[data-bmw-series]');
    if (seriesSelect instanceof HTMLSelectElement) seriesSelect.value = series;
    this.populateChassis(series, record.chassis);

    this.renderRemembered(record);
    this.requestBadgeUpdate();
    const dialog = this.querySelector('[data-bmw-dialog]');
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      result?.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  renderRemembered(record) {
    const prompt = this.querySelector('[data-bmw-prompt]');
    const remembered = this.querySelector('[data-bmw-remembered]');
    prompt?.toggleAttribute('hidden', Boolean(record));
    remembered?.toggleAttribute('hidden', !record);
    if (!record) return;

    const name = this.querySelector('[data-bmw-remembered-vehicle]');
    const chassis = this.querySelector('[data-bmw-remembered-chassis]');
    const diameter = this.querySelector('[data-bmw-remembered-diameter]');
    if (name) name.textContent = recordPrimaryDisplay(record);
    if (chassis) chassis.textContent = record.chassis;
    if (diameter) diameter.textContent = record.diameter;
  }

  async selectDiameter() {
    if (!this.selectedRecord || this.applyingDiameter) return;

    const targetDiameter = normalize(this.selectedRecord.diameter);
    const product = this.closest('.centre-cap-pdp');
    if (!product) return;
    const controls = Array.from(product.querySelectorAll('variant-picker [data-option-name]'));
    const control = controls.find((element) => {
      const optionName = normalize(element.dataset.optionName);
      return optionName === 'DIAMETER' && normalize(element.value) === targetDiameter;
    });

    if (control instanceof HTMLInputElement || control instanceof HTMLOptionElement) {
      if (control.dataset.optionAvailable === 'false' || control.getAttribute('aria-disabled') === 'true') return;
      const picker = control.closest('variant-picker');
      if (!picker) return;
      const alreadySelected = control instanceof HTMLInputElement ? control.checked : control.selected;
      this.applyingDiameter = true;
      const selectButton = this.querySelector('[data-bmw-select-size]');
      if (selectButton instanceof HTMLButtonElement) selectButton.disabled = true;

      try {
        let expectedVariantId = '';
        if (!alreadySelected) {
          const selected = new Promise((resolve) => {
            picker.addEventListener('shopify:product:select', (event) => resolve(event.promise), { once: true });
          });
          if (control instanceof HTMLInputElement) {
            control.click();
          } else if (control.parentElement instanceof HTMLSelectElement) {
            control.parentElement.value = control.value;
            control.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
          }
          const response = await selected;
          if (!response?.detail?.resource?.id || response.detail.resource.available === false) return;
          expectedVariantId = String(response.detail.resource.id);
        }

        // ProductForm also awaits the select event's promise before updating its hidden variant ID.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        const chosen = Array.from(product.querySelectorAll('variant-picker [data-option-name]')).find((option) =>
          normalize(option.dataset.optionName) === 'DIAMETER' && normalize(option.value) === targetDiameter &&
          (option instanceof HTMLInputElement ? option.checked : option.selected)
        );
        const variantId = product.querySelector('product-form-component input[name="id"]')?.value;
        if (!chosen || !variantId || variantId === '0' || (expectedVariantId && variantId !== expectedVariantId)) return;
        if (alreadySelected) {
          const selectedVariant = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || 'null');
          if (!selectedVariant?.available || String(selectedVariant.id) !== variantId) return;
        }
        if (!this.querySelector('[data-bmw-dialog]')?.open) return;

        await this.completeRecommendation(product, targetDiameter);
        return;
      } catch (_error) {
        // A superseding variant request can abort the fetch; keep the finder open.
        return;
      } finally {
        this.applyingDiameter = false;
        if (selectButton instanceof HTMLButtonElement) selectButton.disabled = false;
      }
    }

    const singleValue = product.querySelector(
      ".variant-option--centre-cap-diameter[data-testid='variant-option-single'] .variant-option__swatch-value"
    );
    if (singleValue && normalize(singleValue.textContent) === targetDiameter && this.productData.singleAvailable === true
      && product.querySelector('product-form-component input[name="id"]')?.value) {
      this.applyingDiameter = true;
      try {
        await this.completeRecommendation(product, targetDiameter);
      } finally {
        this.applyingDiameter = false;
      }
    }
  }

  async completeRecommendation(product, diameter) {
    const dialog = this.querySelector('[data-bmw-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    // Native dialog closure restores focus asynchronously. Wait for its close event before
    // moving focus and the storefront scroller, so neither can undo the requested scroll.
    const closed = new Promise((resolve) => dialog.addEventListener('close', resolve, { once: true }));
    this.appliedDiameter = diameter;
    this.closeDialog(false);
    await closed;
    this.requestBadgeUpdate();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (this.isConnected && product.isConnected) this.scrollToDiameter(product);
  }

  scrollToDiameter(product) {
    const diameter = product.querySelector('.variant-option--centre-cap-diameter');
    if (!(diameter instanceof HTMLElement)) return;
    const scrollContainer = getScrollContainer();
    const currentScroll = getScrollTop();
    const containerTop = scrollContainer === document.scrollingElement ? 0 : scrollContainer.getBoundingClientRect().top;
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
    const heading = product.querySelector('.centre-cap-product-header');
    const price = product.querySelector('.product-details product-price');
    const purchase = product.querySelector('product-form-component .product-form-buttons');
    const margin = 16;
    const diameterTop = diameter.getBoundingClientRect().top - containerTop + currentScroll;
    const headingTop = heading instanceof HTMLElement
      ? heading.getBoundingClientRect().top - containerTop + currentScroll
      : diameterTop;
    const priceTop = price instanceof HTMLElement
      ? price.getBoundingClientRect().top - containerTop + currentScroll
      : diameterTop;
    const purchaseBottom = purchase instanceof HTMLElement
      ? purchase.getBoundingClientRect().bottom - containerTop + currentScroll
      : diameterTop + diameter.offsetHeight;
    const availableHeight = scrollContainer.clientHeight - headerHeight - margin * 2;
    const fitsFromHeading = purchaseBottom - headingTop <= availableHeight;
    const fitsFromPrice = purchaseBottom - priceTop <= availableHeight;
    const targetTop = fitsFromHeading ? headingTop : fitsFromPrice ? priceTop : diameterTop;
    const top = Math.max(0, targetTop - headerHeight - margin);
    diameter.setAttribute('tabindex', '-1');
    diameter.focus({ preventScroll: true });
    diameter.addEventListener('blur', () => diameter.removeAttribute('tabindex'), { once: true });
    scrollPage({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  requestBadgeUpdate() {
    window.cancelAnimationFrame(this.badgeFrame);
    this.badgeFrame = window.requestAnimationFrame(() => this.updateBadge());
  }

  updateBadge() {
    const product = this.closest('.centre-cap-pdp') ?? document;
    const existing = product.querySelector('[data-bmw-recommended-badge]');
    if (!this.selectedRecord || this.appliedDiameter !== normalize(this.selectedRecord.diameter)) {
      existing?.remove();
      return;
    }

    const target = normalize(this.selectedRecord.diameter);
    const selectedInput = Array.from(product.querySelectorAll("variant-picker input[data-option-name='Diameter']:checked"))
      .find((input) => normalize(input.value) === target);
    const selectedOption = Array.from(product.querySelectorAll("variant-picker option[data-option-name='Diameter']:checked"))
      .find((option) => normalize(option.value) === target);
    const single = product.querySelector(
      ".variant-option--centre-cap-diameter[data-testid='variant-option-single'] .variant-option__swatch-value"
    );
    const singleMatches = single && normalize(single.textContent) === target;
    const anchor = selectedInput?.closest('.variant-option--centre-cap-diameter')
      ?? selectedOption?.closest('.variant-option--centre-cap-diameter')
      ?? (singleMatches ? single.closest('.variant-option--centre-cap-diameter') : null);

    if (!anchor) {
      existing?.remove();
      return;
    }

    if (existing && existing.previousElementSibling === anchor) return;
    existing?.remove();
    const badge = document.createElement('p');
    badge.className = 'centre-cap-bmw-option-recommendation';
    badge.dataset.bmwRecommendedBadge = '';
    badge.textContent = this.productData.copy?.recommendedBadge ?? '';
    anchor.insertAdjacentElement('afterend', badge);
  }
}

class CentreCapBmwSizeGuide extends HTMLElement {
  abortController;

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.querySelector('[data-bmw-guide-search]')?.addEventListener('input', this.filter, { signal });
    this.querySelector('[data-bmw-guide-series]')?.addEventListener('change', this.filter, { signal });
    this.querySelector('[data-bmw-guide-submit]')?.addEventListener('click', this.filter, { signal });
    this.filter();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  filter = () => {
    const search = this.querySelector('[data-bmw-guide-search]');
    const series = this.querySelector('[data-bmw-guide-series]');
    const summary = this.querySelector('[data-bmw-guide-summary]');
    const empty = this.querySelector('[data-bmw-guide-empty]');
    const groups = this.querySelector('[data-bmw-guide-groups]');
    const resultsHeader = this.querySelector('[data-bmw-guide-results-header]');
    const query = search instanceof HTMLInputElement ? search.value : '';
    const selectedSeries = series instanceof HTMLSelectElement ? series.value : '';
    const isFiltered = Boolean(searchTokens(query).length || selectedSeries);
    let visibleCount = 0;

    const visibleSeries = new Set();
    this.querySelectorAll('[data-bmw-guide-record]').forEach((record) => {
      const matchesSeries = !selectedSeries || record.dataset.series === selectedSeries;
      const matchesQuery = matchesSearchText(record.dataset.search, query, {
        chassis: record.dataset.chassis,
        series: record.dataset.series,
        yearFrom: record.dataset.yearFrom,
        yearTo: record.dataset.yearTo,
        badgeAliases: record.dataset.badgeAliases,
      });
      const isVisible = matchesSeries && matchesQuery;
      const model = record.querySelector('[data-bmw-guide-model]');
      const matchReason = record.querySelector('[data-bmw-guide-match-reason]');
      const matchedAliases = matchedBadgeAliases(record.dataset.badgeAliases, query, record.dataset);
      if (model) model.textContent = customerDisplayLabel(record.dataset.primaryDisplay);
      if (matchReason) {
        const showMatchReason = isVisible && matchedAliases.length && this.dataset.matchReasonLabel;
        matchReason.textContent = showMatchReason
          ? `${this.dataset.matchReasonLabel}: ${matchedAliases.join(', ')}`
          : '';
        matchReason.toggleAttribute('hidden', !showMatchReason);
      }
      record.toggleAttribute('hidden', !isVisible);
      if (isVisible) {
        visibleSeries.add(record.dataset.series);
        visibleCount += 1;
      }
    });

    this.querySelectorAll('[data-bmw-guide-group]').forEach((group) => {
      const hasVisibleRecord = visibleSeries.has(group.dataset.series);
      group.toggleAttribute('hidden', !hasVisibleRecord);
      group.querySelector('[data-bmw-guide-group-header]')?.toggleAttribute('hidden', isFiltered || !hasVisibleRecord);
    });

    if (summary) summary.textContent = `${visibleCount} ${this.dataset.resultsLabel ?? ''}`;
    resultsHeader?.toggleAttribute('hidden', !isFiltered || visibleCount === 0);
    empty?.toggleAttribute('hidden', visibleCount !== 0);
    groups?.toggleAttribute('hidden', visibleCount === 0);
  };
}

if (!customElements.get('centre-cap-bmw-fitment')) {
  customElements.define('centre-cap-bmw-fitment', CentreCapBmwFitment);
}

if (!customElements.get('centre-cap-bmw-size-guide')) {
  customElements.define('centre-cap-bmw-size-guide', CentreCapBmwSizeGuide);
}
